import * as T from './types';
import ProfilesAPI from '../../../api/socialMonitoring/profiles';
import DashboardAPI from '../../../api/socialMonitoring/dashboard';
import InsightsAPI from '../../../api/socialMonitoring/insights';
import ReportsAPI from '../../../api/socialMonitoring/reports';
import SettingsAPI from '../../../api/socialMonitoring/settings';
import AlertsAPI from '../../../api/socialMonitoring/alerts';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 60;

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const pollUntilReady = async request => {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await request();
    if (response.status !== 202) return response.data;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_INTERVAL_MS);
  }
  return null;
};

const pollReportUntilReady = async (getById, id) => {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await getById(id);
    if (data.status !== 'pending') return data;
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_INTERVAL_MS);
  }
  return null;
};

export const actions = {
  async fetchSummary({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingSummary: true });
    try {
      const { data } = await DashboardAPI.summary(opts);
      commit(T.SM_SET_SUMMARY, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingSummary: false });
    }
  },
  async fetchSentiment({ commit }, opts) {
    const { data } = await DashboardAPI.sentiment(opts);
    commit(T.SM_SET_SENTIMENT, data);
  },
  async fetchEngagement({ commit }, opts) {
    const { data } = await DashboardAPI.engagement(opts);
    commit(T.SM_SET_ENGAGEMENT, data);
  },
  async fetchCompetitors({ commit }, opts) {
    const { data } = await DashboardAPI.competitors(opts);
    commit(T.SM_SET_COMPETITORS, data);
  },
  async fetchTopMentioners({ commit }, opts) {
    const { data } = await DashboardAPI.topMentioners(opts);
    commit(T.SM_SET_TOP_MENTIONERS, data);
  },
  async fetchMentions({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingMentions: true });
    try {
      const { data } = await DashboardAPI.mentions(opts);
      commit(T.SM_SET_MENTIONS, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingMentions: false });
    }
  },
  async fetchProfiles({ commit }) {
    commit(T.SM_SET_UI_FLAG, { isFetchingProfiles: true });
    try {
      const { data } = await ProfilesAPI.get();
      commit(T.SM_SET_PROFILES, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingProfiles: false });
    }
  },
  async createProfile({ commit }, payload) {
    commit(T.SM_SET_UI_FLAG, { isCreating: true });
    try {
      const { data } = await ProfilesAPI.create(payload);
      commit(T.SM_ADD_PROFILE, data);
      return data;
    } finally {
      commit(T.SM_SET_UI_FLAG, { isCreating: false });
    }
  },
  async updateProfile({ commit }, { id, ...payload }) {
    const { data } = await ProfilesAPI.update(id, payload);
    commit(T.SM_UPDATE_PROFILE, data);
    return data;
  },
  async toggleProfile({ commit }, id) {
    const { data } = await ProfilesAPI.toggle(id);
    commit(T.SM_UPDATE_PROFILE, data);
    return data;
  },
  async deleteProfile({ commit }, id) {
    await ProfilesAPI.delete(id);
    commit(T.SM_REMOVE_PROFILE, id);
  },
  async collectProfile(_ctx, id) {
    await ProfilesAPI.collect(id);
  },
  async collectAll() {
    await ProfilesAPI.collectAll();
  },
  async fetchSettings({ commit }) {
    const { data } = await SettingsAPI.get();
    commit(T.SM_SET_SETTINGS, data);
  },
  async updateSettings({ commit }, payload) {
    const { data } = await SettingsAPI.create(payload);
    commit(T.SM_SET_SETTINGS, data);
  },
  async fetchPoliticalInsights({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingInsights: true });
    try {
      const data = await pollUntilReady(() => InsightsAPI.political(opts));
      if (data) commit(T.SM_SET_POLITICAL_INSIGHTS, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingInsights: false });
    }
  },
  async fetchPublicReaction({ commit }, opts) {
    const { data } = await InsightsAPI.publicReaction(opts);
    commit(T.SM_SET_PUBLIC_REACTION, data);
  },
  async fetchPublicReactionAnalysis({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingReactionAnalysis: true });
    try {
      const data = await pollUntilReady(() =>
        InsightsAPI.publicReactionAnalysis(opts)
      );
      if (data) commit(T.SM_SET_PUBLIC_REACTION_ANALYSIS, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingReactionAnalysis: false });
    }
  },
  async fetchTopics({ commit }, opts) {
    const { data } = await InsightsAPI.topics(opts);
    commit(T.SM_SET_TOPICS, data);
  },
  async listReports({ commit }, opts) {
    const { data } = await ReportsAPI.list(opts);
    commit(T.SM_SET_REPORTS, data);
  },
  async generateReport({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isGeneratingReport: true });
    try {
      const { data: pending } = await ReportsAPI.generate(opts);
      commit(T.SM_SET_CURRENT_REPORT, pending);
      const ready = await pollReportUntilReady(
        id => ReportsAPI.show(id),
        pending.id
      );
      if (ready) commit(T.SM_SET_CURRENT_REPORT, ready);
      return ready || pending;
    } finally {
      commit(T.SM_SET_UI_FLAG, { isGeneratingReport: false });
    }
  },
  async getReport({ commit }, id) {
    const { data } = await ReportsAPI.show(id);
    commit(T.SM_SET_CURRENT_REPORT, data);
    return data;
  },
  async fetchClusters({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingClusters: true });
    try {
      const { data } = await InsightsAPI.clusters(opts);
      commit(T.SM_SET_CLUSTERS, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingClusters: false });
    }
  },
  async fetchAlerts({ commit }, opts) {
    const { data } = await AlertsAPI.list(opts);
    commit(T.SM_SET_ALERTS, data.items || []);
    commit(T.SM_SET_ALERT_UNREAD, data.unreadCount || 0);
  },
  async acknowledgeAlert({ commit, dispatch }, id) {
    await AlertsAPI.acknowledge(id);
    commit(T.SM_MARK_ALERT_ACK, id);
    dispatch('fetchAlerts');
  },
  async fetchContentBreakdown({ commit }, opts) {
    const { data } = await DashboardAPI.contentBreakdown(opts);
    commit(T.SM_SET_CONTENT_BREAKDOWN, data);
  },
  async acknowledgeAllAlerts({ commit, dispatch }) {
    await AlertsAPI.acknowledgeAll();
    commit(T.SM_SET_ALERT_UNREAD, 0);
    dispatch('fetchAlerts');
  },
  async fetchConversionIndices({ commit }, opts) {
    const { data } = await DashboardAPI.conversionIndices(opts);
    commit(T.SM_SET_CONVERSION_INDICES, data);
  },
  async fetchNarratives({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingNarratives: true });
    try {
      const { data } = await InsightsAPI.narratives(opts);
      commit(T.SM_SET_NARRATIVES, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingNarratives: false });
    }
  },
  async fetchEmotionBreakdown({ commit }, opts) {
    const { data } = await DashboardAPI.emotionBreakdown(opts);
    commit(T.SM_SET_EMOTION_BREAKDOWN, data);
  },
  async fetchTerritorialBreakdown({ commit }, opts) {
    const { data } = await DashboardAPI.territorialBreakdown(opts);
    commit(T.SM_SET_TERRITORIAL_BREAKDOWN, data);
  },
  async fetchDemographicBreakdown({ commit }, opts) {
    const { data } = await DashboardAPI.demographicBreakdown(opts);
    commit(T.SM_SET_DEMOGRAPHIC_BREAKDOWN, data);
  },
  async fetchTerritorialSegmentation({ commit }, opts) {
    const { data } = await DashboardAPI.territorialSegmentation(opts);
    commit(T.SM_SET_TERRITORIAL_SEGMENTATION, data);
  },
  async fetchContentSuggestions({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingSuggestions: true });
    try {
      const initial = await InsightsAPI.contentSuggestions(opts);
      if (initial.status !== 202) {
        commit(T.SM_SET_CONTENT_SUGGESTIONS, initial.data);
        return;
      }
      const data = await pollUntilReady(() =>
        InsightsAPI.contentSuggestions({ ...opts, refresh: false })
      );
      if (data) commit(T.SM_SET_CONTENT_SUGGESTIONS, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingSuggestions: false });
    }
  },
  async fetchResponseLibrary({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingResponseLibrary: true });
    try {
      const { data } = await InsightsAPI.responseLibrary(opts);
      commit(T.SM_SET_RESPONSE_LIBRARY, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingResponseLibrary: false });
    }
  },
  async fetchCopilotLatest({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isFetchingCopilot: true });
    try {
      const { data } = await InsightsAPI.copilotLatest(opts);
      commit(T.SM_SET_COPILOT, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isFetchingCopilot: false });
    }
  },
  async runCopilot({ commit }, opts) {
    commit(T.SM_SET_UI_FLAG, { isRunningCopilot: true });
    try {
      const { data } = await InsightsAPI.copilotRun(opts);
      commit(T.SM_SET_COPILOT, data);
    } finally {
      commit(T.SM_SET_UI_FLAG, { isRunningCopilot: false });
    }
  },
};
